import { pick } from 'lodash'

export type ApiRequestOptions = {
  body?: string
  method?: string
  checkOk?: boolean
  parseJson?: boolean
  requireToken?: boolean
  useResponseMessage?: boolean
  headers?: { [k: string]: string }
}

/**
 *
 * @param {String} url Some url.
 * @param {Object} options Same as options for fetch(), extended by requireToken option.
 * If requireToken is set to true, Authorization header will be added to the request.
 *
 * The authorization headSer is added to the request after a successful check of user login and
 * application authorization.
 *
 */
export async function apiRequest(url: string, options: ApiRequestOptions) {
  if (options?.requireToken) {
    return fetchWithAuthorization(url, options)
  }

  const response = await fetch(url, pick(options || {}, ['body', 'method', 'headers']))
  return getResponse(url, response, options)
}

export async function fetchWithAuthorization(
  url: string,
  options: ApiRequestOptions
): Promise<any> {
  const optionsWithAuthorization = await getOptionsWithAuthorization(options)
  const response = await fetch(url, optionsWithAuthorization)

  return getResponse(url, response, optionsWithAuthorization)
}

async function getOptionsWithAuthorization(options: ApiRequestOptions): Promise<ApiRequestOptions> {
  const { getAccessToken } = await import('./getAccessToken')
  const { getForcedAuthHeaders } = await import('./getForcedAuthHeaders')

  const additionalHeaders = {
    authorization: 'Bearer ' + getAccessToken(),
    ...getForcedAuthHeaders()
  }

  return {
    ...options,
    headers: { ...options.headers, ...additionalHeaders }
  }
}

async function getResponse(url: string, response: Response, options: ApiRequestOptions) {
  const { checkOk, parseJson = true, useResponseMessage } = options

  if (!checkOk) {
    return response
  }

  if (!response.ok) {
    await throwResponseException(url, response, useResponseMessage)
  }

  if (parseJson) {
    return response.json()
  }

  return response
}

async function throwResponseException(
  url: string,
  response: Response,
  useResponseMessage?: boolean
) {
  if (!useResponseMessage) {
    throw new Error(`${url} HTTP response error`)
  }

  const status = response.status
  const text = await response.text()

  throw new Error(`${text}; status=${status}`)
}
